import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";

// Types
export type Friend = {
  id: string;
  userId: string;
  friendId: string;
  status: 'pending' | 'accepted' | 'rejected' | 'active';
  createdAt: string;
  alias?: string | null;
  displayName?: string | null;
  friend: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    username?: string | null;
  };
};

export type FriendInvitation = {
  id: string;
  inviterId: string;
  inviteeEmail: string;
  message: string | null;
  status: 'pending' | 'accepted' | 'rejected' | 'cancelled' | 'expired';
  relationshipType?: 'friend' | 'family' | string;
  createdAt: string;
  expiresAt?: string | null;
  displayName?: string | null;
  alias?: string | null;
  fromUser?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  toUser?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
};

export type SharedGoal = {
  id: string;
  goalId: string;
  ownerId: string;
  sharedWithUserId: string;
  permission: 'view' | 'edit';
  createdAt: string;
  goal: {
    id: string;
    title: string;
    description: string | null;
    status: string;
    progress: number;
  };
  owner: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
};

export type SharedTask = {
  id: string;
  taskId: string;
  ownerId: string;
  sharedWithUserId: string;
  permission: 'view' | 'edit';
  createdAt: string;
  task: {
    id: string;
    title: string;
    description: string | null;
    completed: boolean;
    priority: string;
  };
  owner: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
};

export type SharedNote = {
  id: string;
  noteId: string;
  ownerId: string;
  sharedWithId: string;
  permission: 'view' | 'edit';
  createdAt: string;
  note: {
    id: string;
    title: string;
    plainContent: string;
    tags: string[];
    type: string;
  };
  owner: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    username: string | null;
    profileImageUrl: string | null;
  };
};

export type Conversation = {
  id: string;
  participant1Id: string;
  participant2Id: string;
  lastMessageAt: string;
  unreadCount: number;
  otherParticipant: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    username?: string | null;
  };
};

export type Message = {
  id: string;
  conversationId: string;
  senderId: string;
  content: string;
  read: boolean;
  createdAt: string;
  sender: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
};

// Friends Queries
export function useFriends() {
  return useQuery<Friend[]>({
    queryKey: ['/api/friends'],
  });
}

export function useSentInvitations() {
  return useQuery<FriendInvitation[]>({
    queryKey: ['/api/friend-invitations/sent'],
    staleTime: 30 * 1000, // Refresh every 30 seconds to catch accepted invitations
    refetchOnWindowFocus: true, // Refresh when user switches back to this tab
    refetchInterval: 60 * 1000, // Auto-refresh every 60 seconds
  });
}

export function useReceivedInvitations() {
  return useQuery<FriendInvitation[]>({
    queryKey: ['/api/friend-invitations/received'],
  });
}

// Shared Items Queries
export function useSharedGoals() {
  return useQuery<SharedGoal[]>({
    queryKey: ['/api/shared-goals'],
  });
}

export function useSharedTasks() {
  return useQuery<SharedTask[]>({
    queryKey: ['/api/shared-tasks'],
  });
}

export function useSharedNotes() {
  return useQuery<SharedNote[]>({
    queryKey: ['/api/shares/notes/received'],
  });
}

export function useGoalShares(goalId: string, options?: { enabled?: boolean }) {
  return useQuery<SharedGoal[]>({
    queryKey: ['/api/goals', goalId, 'shares'],
    enabled: !!goalId && (options?.enabled ?? true),
  });
}

export function useTaskShares(taskId: string, options?: { enabled?: boolean }) {
  return useQuery<SharedTask[]>({
    queryKey: ['/api/tasks', taskId, 'shares'],
    enabled: !!taskId && (options?.enabled ?? true),
  });
}

// Messaging Queries
export function useConversations() {
  return useQuery<Conversation[]>({
    queryKey: ['/api/messages/conversations'],
    refetchInterval: 5000, // Poll every 5 seconds for new messages
  });
}

export function useArchivedConversations() {
  return useQuery<Conversation[]>({
    queryKey: ['/api/messages/conversations/archived'],
    refetchInterval: 30000,
  });
}

export function useDeleteConversation() {
  return useMutation({
    mutationFn: (conversationId: string) =>
      apiRequest('DELETE', `/api/messages/conversations/${conversationId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/messages/conversations'] });
      queryClient.invalidateQueries({ queryKey: ['/api/messages/conversations/archived'] });
    },
  });
}

export function useArchiveConversation() {
  return useMutation({
    mutationFn: ({ conversationId, archive }: { conversationId: string; archive: boolean }) =>
      apiRequest('PATCH', `/api/messages/conversations/${conversationId}/archive`, { archive }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/messages/conversations'] });
      queryClient.invalidateQueries({ queryKey: ['/api/messages/conversations/archived'] });
    },
  });
}

export function useMessages(conversationId: string | null) {
  return useQuery<Message[]>({
    queryKey: ['/api/messages/conversations', conversationId, 'messages'],
    enabled: !!conversationId,
    refetchInterval: 5000, // Poll every 5 seconds for new messages
  });
}

// Friend Invitations Mutations
export function useSendInvitation() {
  return useMutation({
    mutationFn: async (data: { email?: string; username?: string; message?: string; displayName?: string; alias?: string; relationshipType?: 'friend' | 'family' }) => {
      const res = await apiRequest('POST', '/api/friend-invitations', data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/friend-invitations/sent'] });
    },
  });
}

export function useUpdateFriendInvitation() {
  return useMutation({
    mutationFn: (data: { invitationId: string; displayName?: string; alias?: string }) =>
      apiRequest('PATCH', `/api/friend-invitations/${data.invitationId}`, { displayName: data.displayName, alias: data.alias }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/friend-invitations/sent'] });
    },
  });
}

export function useUpdateFriendship() {
  return useMutation({
    mutationFn: (data: { friendshipId: string; displayName?: string; alias?: string }) =>
      apiRequest('PATCH', `/api/friends/${data.friendshipId}`, { displayName: data.displayName, alias: data.alias }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/friends'] });
    },
  });
}

export function useAcceptInvitation() {
  return useMutation({
    mutationFn: (invitationId: string) =>
      apiRequest('POST', `/api/friend-invitations/${invitationId}/accept`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/friend-invitations/received'] });
      queryClient.invalidateQueries({ queryKey: ['/api/friends'] });
    },
  });
}

export function useRejectInvitation() {
  return useMutation({
    mutationFn: (invitationId: string) =>
      apiRequest('POST', `/api/friend-invitations/${invitationId}/reject`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/friend-invitations/received'] });
    },
  });
}

export function useCancelInvitation() {
  return useMutation({
    mutationFn: (invitationId: string) =>
      apiRequest('DELETE', `/api/friend-invitations/${invitationId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/friend-invitations/sent'] });
    },
  });
}

export function useResendInvitation() {
  return useMutation({
    mutationFn: async (invitationId: string) => {
      const res = await apiRequest('POST', `/api/friend-invitations/${invitationId}/resend`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/friend-invitations/sent'] });
    },
  });
}

export function useRemoveFriend() {
  return useMutation({
    mutationFn: (friendId: string) =>
      apiRequest('DELETE', `/api/friends/${friendId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/friends'] });
    },
  });
}

export function useHideFriend() {
  return useMutation({
    mutationFn: ({ friendshipId, reason }: { friendshipId: string; reason?: string }) =>
      apiRequest('POST', `/api/friends/${friendshipId}/hide`, { reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/friends'] });
      queryClient.invalidateQueries({ queryKey: ['/api/friends/hidden'] });
      queryClient.invalidateQueries({ queryKey: ['/api/messages/conversations'] });
    },
  });
}

export function useUnhideFriend() {
  return useMutation({
    mutationFn: (friendshipId: string) =>
      apiRequest('POST', `/api/friends/${friendshipId}/unhide`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/friends'] });
      queryClient.invalidateQueries({ queryKey: ['/api/friends/hidden'] });
      queryClient.invalidateQueries({ queryKey: ['/api/messages/conversations'] });
    },
  });
}

export function useHiddenFriends() {
  return useQuery<Friend[]>({
    queryKey: ['/api/friends/hidden'],
  });
}

// Sharing Mutations
export function useShareGoal() {
  return useMutation({
    mutationFn: (data: { goalId: string; friendId: string; permission: 'view' | 'edit' }) =>
      apiRequest('POST', '/api/share-goal', data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['/api/goals', variables.goalId, 'shares'] });
    },
  });
}

export function useUnshareGoal() {
  return useMutation({
    mutationFn: (shareId: string) =>
      apiRequest('DELETE', `/api/shared-goals/${shareId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/goals'] });
    },
  });
}

export function useShareTask() {
  return useMutation({
    mutationFn: (data: { taskId: string; friendId: string; permission: 'view' | 'edit' }) =>
      apiRequest('POST', '/api/share-task', data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['/api/tasks', variables.taskId, 'shares'] });
    },
  });
}

export function useUnshareTask() {
  return useMutation({
    mutationFn: (shareId: string) =>
      apiRequest('DELETE', `/api/shared-tasks/${shareId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/tasks'] });
    },
  });
}

export function useUpdateSharedTask() {
  return useMutation({
    mutationFn: (data: {
      taskId: string;
      completed?: boolean;
      title?: string;
      description?: string | null;
      dueDate?: string | null;
    }) => {
      const { taskId, ...fields } = data;
      return apiRequest('PATCH', `/api/shared-tasks/${taskId}/update`, fields);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/shared-tasks'] });
    },
  });
}

export function useShareNote() {
  return useMutation({
    mutationFn: (data: { noteId: string; friendId: string; permission: 'view' | 'edit' }) =>
      apiRequest('POST', '/api/shares/notes', {
        noteId: data.noteId,
        sharedWithId: data.friendId,
        permission: data.permission
      }),
    onSuccess: (_, variables) => {
      // The share dialog reads from '/api/shares/notes/sent' — with
      // staleTime: Infinity it must be invalidated here or the dialog
      // keeps showing the pre-share (empty) list forever.
      queryClient.invalidateQueries({ queryKey: ['/api/shares/notes/sent'] });
      queryClient.invalidateQueries({ queryKey: ['/api/notes', variables.noteId, 'shares'] });
      queryClient.invalidateQueries({ queryKey: ['/api/notes'] });
    },
  });
}

export function useUnshareNote() {
  return useMutation({
    mutationFn: (shareId: string) =>
      apiRequest('DELETE', `/api/shares/notes/${shareId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/shares/notes/sent'] });
      queryClient.invalidateQueries({ queryKey: ['/api/notes'] });
    },
  });
}

export function useNoteShares(noteId: string, options?: { enabled?: boolean }) {
  return useQuery<any[]>({
    queryKey: ['/api/shares/notes/sent'],
    enabled: !!noteId && (options?.enabled ?? true),
    select: (data) => data.filter((share: any) => share.noteId === noteId)
  });
}

// Calendar Event Sharing
export function useShareCalendarEvent() {
  return useMutation({
    mutationFn: (data: { calendarEventId: string; friendId: string; permission: 'view' | 'edit' }) =>
      apiRequest('POST', '/api/share-calendar-event', {
        calendarEventId: data.calendarEventId,
        sharedWithId: data.friendId,
        permission: data.permission
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['/api/calendar-events', variables.calendarEventId, 'shares'] });
      queryClient.invalidateQueries({ queryKey: ['/api/calendar'] });
    },
  });
}

export function useUnshareCalendarEvent() {
  return useMutation({
    mutationFn: (shareId: string) =>
      apiRequest('DELETE', `/api/shared-calendar-events/${shareId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/calendar'] });
      queryClient.invalidateQueries({ queryKey: ['/api/calendar-events'] });
    },
  });
}

export function useCalendarEventShares(calendarEventId: string, options?: { enabled?: boolean }) {
  return useQuery<any[]>({
    queryKey: ['/api/calendar-events', calendarEventId, 'shares'],
    enabled: !!calendarEventId && (options?.enabled ?? true),
  });
}

// Messaging Mutations
export function useSendMessage() {
  return useMutation({
    mutationFn: (data: { conversationId?: string; recipientId?: string; content: string }) =>
      apiRequest('POST', '/api/messages', data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['/api/messages/conversations'] });
      if (variables.conversationId) {
        queryClient.invalidateQueries({ 
          queryKey: ['/api/messages/conversations', variables.conversationId, 'messages'] 
        });
      }
    },
  });
}

export function useMarkMessagesAsRead() {
  return useMutation({
    mutationFn: (conversationId: string) =>
      apiRequest('POST', `/api/messages/conversations/${conversationId}/mark-read`),
    onSuccess: (_, conversationId) => {
      queryClient.invalidateQueries({ queryKey: ['/api/messages/conversations'] });
      queryClient.invalidateQueries({ 
        queryKey: ['/api/messages/conversations', conversationId, 'messages'] 
      });
    },
  });
}

// Chat Room Types
export type ChatRoomMember = {
  id: string;
  chatRoomId: string;
  userId: string;
  role: 'admin' | 'member';
  joinedAt: string;
  user?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
};

export type ChatRoom = {
  id: string;
  tenantId: string;
  name: string;
  description: string | null;
  createdById: string;
  createdAt: string;
  updatedAt: string;
  members: ChatRoomMember[];
};

export type ChatRoomMessage = {
  id: string;
  chatRoomId: string;
  senderId: string;
  content: string;
  createdAt: string;
  sender?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
};

// Chat Room Queries
export function useChatRooms() {
  return useQuery<ChatRoom[]>({
    queryKey: ['/api/chat-rooms'],
    refetchInterval: 10000,
  });
}

export function useChatRoom(roomId: string | null) {
  return useQuery<ChatRoom>({
    queryKey: ['/api/chat-rooms', roomId],
    enabled: !!roomId,
  });
}

export function useChatRoomMessages(roomId: string | null) {
  return useQuery<ChatRoomMessage[]>({
    queryKey: ['/api/chat-rooms', roomId, 'messages'],
    enabled: !!roomId,
    refetchInterval: 5000,
  });
}

// Chat Room Mutations
export function useCreateChatRoom() {
  return useMutation({
    mutationFn: (data: { name: string; description?: string; memberIds: string[] }) =>
      apiRequest('POST', '/api/chat-rooms', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/chat-rooms'] });
    },
  });
}

export function useSendChatRoomMessage() {
  return useMutation({
    mutationFn: (data: { roomId: string; content: string }) =>
      apiRequest('POST', `/api/chat-rooms/${data.roomId}/messages`, { content: data.content }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['/api/chat-rooms', variables.roomId, 'messages'] });
    },
  });
}

export function useAddChatRoomMember() {
  return useMutation({
    mutationFn: (data: { roomId: string; userId: string }) =>
      apiRequest('POST', `/api/chat-rooms/${data.roomId}/members`, { userId: data.userId }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['/api/chat-rooms', variables.roomId] });
      queryClient.invalidateQueries({ queryKey: ['/api/chat-rooms'] });
    },
  });
}

export function useLeaveChatRoom() {
  return useMutation({
    mutationFn: (data: { roomId: string; userId: string }) =>
      apiRequest('DELETE', `/api/chat-rooms/${data.roomId}/members/${data.userId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/chat-rooms'] });
    },
  });
}

export function useUpdateChatRoom() {
  return useMutation({
    mutationFn: (data: { roomId: string; name?: string; description?: string }) =>
      apiRequest('PATCH', `/api/chat-rooms/${data.roomId}`, { name: data.name, description: data.description }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['/api/chat-rooms', variables.roomId] });
      queryClient.invalidateQueries({ queryKey: ['/api/chat-rooms'] });
    },
  });
}

export function useDeleteChatRoom() {
  return useMutation({
    mutationFn: (roomId: string) =>
      apiRequest('DELETE', `/api/chat-rooms/${roomId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/chat-rooms'] });
    },
  });
}

export function useRemoveChatRoomMember() {
  return useMutation({
    mutationFn: (data: { roomId: string; userId: string }) =>
      apiRequest('DELETE', `/api/chat-rooms/${data.roomId}/members/${data.userId}`),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['/api/chat-rooms', variables.roomId] });
      queryClient.invalidateQueries({ queryKey: ['/api/chat-rooms'] });
    },
  });
}

// Friend Groups Types
export type FriendGroup = {
  id: string;
  tenantId: string;
  userId: string;
  name: string;
  color: string | null;
  sortOrder: number;
  createdAt: string;
};

export type FriendGroupMember = {
  id: string;
  tenantId: string;
  friendGroupId: string;
  friendshipId: string;
  createdAt: string;
};

// Friend Groups Queries
export function useFriendGroups() {
  return useQuery<FriendGroup[]>({
    queryKey: ['/api/friend-groups'],
  });
}

export function useFriendGroupMembers(groupId: string | null) {
  return useQuery<FriendGroupMember[]>({
    queryKey: ['/api/friend-groups', groupId, 'members'],
    enabled: !!groupId,
  });
}

export function useFriendshipGroups(friendshipId: string | null) {
  return useQuery<FriendGroup[]>({
    queryKey: ['/api/friends', friendshipId, 'groups'],
    enabled: !!friendshipId,
  });
}

// Friend Groups Mutations
export function useCreateFriendGroup() {
  return useMutation({
    mutationFn: (data: { name: string; color?: string; sortOrder?: number }) =>
      apiRequest('POST', '/api/friend-groups', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/friend-groups'] });
    },
  });
}

export function useUpdateFriendGroup() {
  return useMutation({
    mutationFn: (data: { groupId: string; name?: string; color?: string; sortOrder?: number }) =>
      apiRequest('PATCH', `/api/friend-groups/${data.groupId}`, { 
        name: data.name, 
        color: data.color, 
        sortOrder: data.sortOrder 
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/friend-groups'] });
    },
  });
}

export function useDeleteFriendGroup() {
  return useMutation({
    mutationFn: (groupId: string) =>
      apiRequest('DELETE', `/api/friend-groups/${groupId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/friend-groups'] });
    },
  });
}

export function useAddFriendToGroup() {
  return useMutation({
    mutationFn: (data: { groupId: string; friendshipId: string }) =>
      apiRequest('POST', `/api/friend-groups/${data.groupId}/members`, { friendshipId: data.friendshipId }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['/api/friend-groups', variables.groupId, 'members'] });
      queryClient.invalidateQueries({ queryKey: ['/api/friend-groups'] });
      queryClient.invalidateQueries({ queryKey: ['/api/friends'] });
    },
  });
}

export function useRemoveFriendFromGroup() {
  return useMutation({
    mutationFn: (data: { groupId: string; friendshipId: string }) =>
      apiRequest('DELETE', `/api/friend-groups/${data.groupId}/members/${data.friendshipId}`),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['/api/friend-groups', variables.groupId, 'members'] });
      queryClient.invalidateQueries({ queryKey: ['/api/friend-groups'] });
      queryClient.invalidateQueries({ queryKey: ['/api/friends'] });
    },
  });
}
